// Chat-row reading shared by the chat panel and the patient-home preview.
// The patient app (messages.html, js/sections/messages.js) must apply the same chatSender rule.

import {
  matchMilestone, isMilestoneSystemRow, isMilestonePing, type MilestoneModel,
} from '../../../scripts/milestone-messages.mjs';
import {
  isSpruceRow, isSpruceEvent, answersPatient, spruceRowTimeMs, spruceAuthorFirstName, spruceEventLabel, spruceTextCaption,
  isSpruceTranscript, spruceDrawable, spruceTranscriptFor, SPRUCE_SOURCE,
} from '../../../scripts/spruce-rows.mjs';
import { isLabCardRow } from './labResults';
import { isCheckinReply } from './checkin';

export {
  isSpruceRow, isSpruceEvent, spruceAuthorFirstName, spruceEventLabel, spruceTextCaption, isSpruceTranscript, SPRUCE_SOURCE,
};

// A clinic-line row no screen draws on its own: a transcript (drawn under its call) or a type this app does not know.
export function isHiddenClinicLineRow(row: ChatRowLike): boolean {
  return isSpruceRow(row) && !spruceDrawable(row);
}

export const AVA_NAME = 'Ava';
export const AVA_ROLE = 'Your Care Guide (AI)';

export type ChatSenderKind = 'patient' | 'staff' | 'ava';

export interface ChatSender {
  kind: ChatSenderKind;
  urgent: boolean;
  // Ava rows only: whether a model wrote the words or code did.
  origin: 'ai' | 'automated' | null;
}

export interface ChatRowLike {
  direction?: string | null;
  source?: string | null;
  msg_type?: string | null;
  message_type?: string | null;
  [key: string]: unknown;
}

const STAFF_SOURCES = new Set(['staff', 'care_team', 'spruce']);
const AI_SOURCES = new Set(['ai_agent', 'ai']);
const CRISIS_TYPES = new Set(['crisis', 'crisis_alert']);

// WF2 writes crisis rows as source 'crisis_alert' + msg_type 'crisis'.
export function isUrgentChatRow(row: ChatRowLike): boolean {
  return row.source === 'crisis_alert'
    || CRISIS_TYPES.has(String(row.msg_type ?? ''))
    || CRISIS_TYPES.has(String(row.message_type ?? ''));
}

// Keyed on direction + source, never an author name, so an automated row can't lose its AI label.
export function chatSender(row: ChatRowLike): ChatSender {
  if (row.direction === 'inbound' || row.source === 'patient') return { kind: 'patient', urgent: false, origin: null };
  const urgent = isUrgentChatRow(row);
  if (STAFF_SOURCES.has(String(row.source ?? ''))) return { kind: 'staff', urgent, origin: null };
  return { kind: 'ava', urgent, origin: AI_SOURCES.has(String(row.source ?? '')) ? 'ai' : 'automated' };
}

export function initialsOf(first: string | null | undefined, last: string | null | undefined, fallback: string): string {
  const f = (first || '').trim()[0] || '';
  const l = (last || '').trim()[0] || '';
  return (f + l).toUpperCase() || fallback;
}

export function fullNameOf(first: string | null | undefined, last: string | null | undefined): string {
  return [first, last].filter(Boolean).join(' ').trim();
}

export const CONTENT_URL_PATTERN = /https:\/\/sparks\.withings\.com\/sparks\/access-content\/content\/([a-z0-9-]+)\.html/;

export interface ContentCardData {
  title: string;
  slug: string;
  url: string;
  // The message body itself, minus the "📖 Title" and URL lines the card replaces.
  text: string;
}

// A content share is "<note>\n\n📖 <title>\n<url>" (ChatPanel's buildContentMessage).
export function parseContentCard(body: string | null | undefined): ContentCardData | null {
  if (!body) return null;
  const match = body.match(CONTENT_URL_PATTERN);
  if (!match) return null;
  const [url, slug = ''] = match;
  const lines = body.split('\n');
  const stripBook = (line: string) => line.replace(/^📖\s*/, '').trim();
  const titleLine = lines.find((line, i) => {
    const cleaned = stripBook(line);
    return cleaned && !cleaned.startsWith('http') && lines[i + 1]?.includes(url);
  });
  const title = titleLine
    ? stripBook(titleLine)
    : slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const text = lines.filter((l) => !CONTENT_URL_PATTERN.test(l) && !/^📖\s/.test(l)).join('\n').trim();
  return {
    title, slug, url, text,
  };
}

// ── Read ticks (spec 2026-10-13-chat-read-receipt) ──
// patient_last_seen_at is a watermark: the server `created` second of the newest row the patient's chat drew.

export type AppSeenState = 'seen' | 'not_seen';

// The patient app's own filter (access-patient js/clinical-api.js listOutreach): change both together.
const HIDDEN_IN_APP_CHANNELS = new Set(['email', 'ehr_message']);

function shownInPatientApp(row: ChatRowLike): boolean {
  return !HIDDEN_IN_APP_CHANNELS.has(String(row.channel ?? '')) && row.msg_type !== 'chat_notification';
}

function createdSecond(row: ChatRowLike): number | null {
  const ms = Date.parse(String(row.created ?? row.created_at ?? ''));
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

// The other rows that tell the patient to call 911: WF2 lane 4's emergency acknowledgement and WF78's red check-in reply.
function isEmergencyReply(row: ChatRowLike): boolean {
  const subject = String(row.subject ?? '');
  return (row.msg_type === 'acknowledgement' && subject.includes('ack:emergency'))
    || (row.msg_type === 'checkin_reply' && subject.split(':')[2] === 'red');
}

// Never on a crisis or emergency row (a tick must not stand in for crisis follow-up), never from sent_at (a sender sets it).
// `threadRows` = the server rows before ChatPanel merges portal/SMS copies, so the same-second count sees every row.
export function appSeenStates<T extends ChatRowLike>(
  rows: T[],
  patientLastSeenAt: string | null | undefined,
  threadRows: ChatRowLike[] = rows,
): Map<T, AppSeenState> {
  const states = new Map<T, AppSeenState>();
  const lastSeenMs = Date.parse(String(patientLastSeenAt ?? ''));
  if (!Number.isFinite(lastSeenMs)) return states;
  const watermark = Math.floor(lastSeenMs / 1000);
  // A second row stored in the watermark's second may have landed after the app's fetch.
  const inWatermarkSecond = threadRows.filter((r) => shownInPatientApp(r) && createdSecond(r) === watermark).length;
  rows.forEach((row) => {
    const second = createdSecond(row);
    const sender = chatSender(row);
    // A carrier text or a call is not an in-app read; it still counts in the watermark second above.
    if (second == null || sender.kind === 'patient' || sender.urgent || isEmergencyReply(row) || !shownInPatientApp(row) || isSpruceRow(row)) return;
    const seen = second < watermark || (second === watermark && inWatermarkSecond === 1);
    states.set(row, seen ? 'seen' : 'not_seen');
  });
  return states;
}

// ── Patient milestone badges (WF80; spec 2026-10-09-patient-milestone-badges) ──
// The wording and the exact-match rule are the shared module the workflows and the badge kit use.

export type { MilestoneModel } from '../../../scripts/milestone-messages.mjs';

export interface MilestonePing {
  status: 'sent' | 'failed';
  at: string | null;
}

// A voicemail's or an answered call's transcript: the one row WF84 wrote for it, or a conflict (two qualify, neither is shown).
export type SpruceTranscriptAttachment<T extends ChatRowLike = ChatRowLike> = { kind: 'one'; row: T } | { kind: 'conflict' };

export type ChatRow<T extends ChatRowLike = ChatRowLike> = T & {
  ping?: MilestonePing | null;
  transcript?: SpruceTranscriptAttachment<T> | null;
};

// A badge row is drawn as one only when its body equals a known version of its approved wording, and
// names this patient when we know their first name.
export function milestoneOf(row: ChatRowLike, expectedFirstName?: string | null): MilestoneModel | null {
  return matchMilestone(row, expectedFirstName ? { expectedFirstName } : undefined);
}

// WF2 lane 4's already_answered rule: a patient question before one of these rows is still open.
export function answersNothing(row: ChatRowLike): boolean {
  return isMilestoneSystemRow(row)
    || isLabCardRow(row)
    || isCheckinReply(row)
    || (row.source === 'system' && row.msg_type === 'visit_join_link')
    || (row.source === 'ai_agent' && row.msg_type === 'acknowledgement')
    || (isSpruceEvent(row) && !answersPatient(row));
}

// Suggest's switch. A clinic-line call never asks; an incoming answered one closes it, an outgoing one never does
// (it may have reached voicemail). A clinic-line text answered in the same
// instant stays open (it is written late, so thread order proves nothing); chat rows keep plain thread order.
export function awaitsCareTeamReply(rows: readonly ChatRowLike[] | null | undefined): boolean {
  const relevant = (rows || []).filter((r) => {
    if (answersNothing(r)) return false;
    if (!isSpruceRow(r)) return true;
    return answersPatient(r) || (r.direction === 'inbound' && r.msg_type === 'spruce_sms');
  });
  // An answered call the patient placed is inbound, yet it answers.
  const asks = (r: ChatRowLike) => !answersPatient(r) && chatSender(r).kind === 'patient';
  const last = relevant[relevant.length - 1];
  if (!last) return false;
  if (asks(last)) return true;
  const at = spruceRowTimeMs(last);
  return Number.isFinite(at) && relevant.some((r) => isSpruceRow(r) && asks(r) && spruceRowTimeMs(r) === at);
}

const MERGE_WINDOW_MS = 5000;

function mergeKey(row: ChatRowLike): string {
  const t = row.sent_at ? Math.floor(new Date(String(row.sent_at)).getTime() / MERGE_WINDOW_MS) : '';
  return `${row.source}|${row.direction}|${t}|${String(row.body ?? '').slice(0, 60)}`;
}

function bodyKey(row: ChatRowLike): string {
  return `${row.direction}|${String(row.body ?? '').slice(0, 60)}`;
}

export interface MergedChat<T extends ChatRowLike, O extends ChatRowLike> {
  rows: Array<T | O>;
  // The optimistic rows no server row has matched yet.
  pending: O[];
}

// `comms` newest first (the server order). A message's portal and SMS copies become one bubble; a badge
// and a clinic-line row are each unique, so they skip the merge and never stand in for an optimistic send.
export function mergeChatRows<T extends ChatRowLike, O extends ChatRowLike>(
  comms: readonly T[] | null | undefined,
  optimistic: readonly O[] | null | undefined,
): MergedChat<T, O> {
  const sorted = [...(comms || [])].reverse();
  const deduped: T[] = [];
  const byKey = new Map<string, T>();
  sorted.forEach((msg) => {
    if (milestoneOf(msg) || isSpruceRow(msg)) {
      deduped.push({ ...msg });
      return;
    }
    const key = mergeKey(msg);
    const existing = byKey.get(key);
    if (existing) {
      if (msg.channel === 'sms') (existing as ChatRowLike).channel = 'sms';
      return;
    }
    const copy = { ...msg };
    byKey.set(key, copy);
    deduped.push(copy);
  });
  const serverBodies = new Set((comms || []).filter((d) => !isSpruceRow(d)).map(bodyKey));
  const pending = (optimistic || []).filter((o) => !serverBodies.has(bodyKey(o)));
  return { rows: [...deduped, ...pending], pending };
}

const PING_SENT = new Set(['sent', 'delivered']);
const PING_FAILED = new Set(['failed', 'undelivered', 'error']);

// The chat thread: email audit rows and "new message" pings dropped, each badge row carrying its own ping.
// A badge and its ping share a per-badge trigger_source, and WF80 sends each badge once per care period.
// A transcript leaves the list and rides on its voicemail or call, matched by payload_ref and time (never by position).
export function toChatThread<T extends ChatRowLike>(rows: T[] | null | undefined): ChatRow<T>[] {
  const all = rows || [];
  const pings = all.filter((r) => isMilestonePing(r));
  const transcripts = all.filter((r) => isSpruceTranscript(r));
  return all
    .filter((r) => r.channel !== 'email' && r.msg_type !== 'chat_notification' && !isHiddenClinicLineRow(r))
    .map((r) => {
      if (isSpruceEvent(r)) {
        const m = spruceTranscriptFor(r, transcripts);
        if (!m) return r;
        const transcript: SpruceTranscriptAttachment<T> = m.conflict || !m.row ? { kind: 'conflict' } : { kind: 'one', row: m.row };
        return { ...r, transcript };
      }
      if (!isMilestoneSystemRow(r)) return r;
      const mine = pings
        .filter((p) => p.trigger_source === r.trigger_source && String(p.id_care_period ?? '') === String(r.id_care_period ?? ''))
        .sort((a, b) => (Date.parse(String(a.sent_at ?? '')) || 0) - (Date.parse(String(b.sent_at ?? '')) || 0));
      // A ping we cannot read as sent or failed is shown as no text yet, never as a failure.
      const p = mine.find((x) => PING_SENT.has(String(x.status))) || mine.find((x) => PING_FAILED.has(String(x.status)));
      const at = p?.sent_at ? String(p.sent_at) : null;
      const ping: MilestonePing | null = p ? { status: PING_SENT.has(String(p.status)) ? 'sent' : 'failed', at } : null;
      return { ...r, ping };
    });
}
